import React, { useState } from 'react';
import { TAB_TYPES } from './tabTypes';
import { describeConfigError, validateTab } from '../../../utils/cockpitConfigValidator';

const typeBadgeClass = 'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300';

// Colonne gauche : liste ordonnée des onglets avec réordonnancement par drag & drop
const TabList = ({ tabs, selectedIndex, onSelect, onAdd, onReorder }) => {
  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  const handleDragStart = (index) => (e) => {
    setDragIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (index) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (index !== dragOverIndex) setDragOverIndex(index);
  };

  const handleDrop = (index) => (e) => {
    e.preventDefault();
    if (dragIndex !== null && dragIndex !== index) {
      onReorder(dragIndex, index);
    }
    setDragIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDragIndex(null);
    setDragOverIndex(null);
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto space-y-2 pr-1">
        {tabs.map((tab, index) => {
          const isSelected = index === selectedIndex;
          const isDragging = index === dragIndex;
          const isDragOver = index === dragOverIndex && dragIndex !== null && dragIndex !== index;
          // Tous les onglets à reprendre, pas seulement le premier signalé en haut du panneau
          const validation = validateTab(tab, index);

          return (
            <button
              key={index}
              type="button"
              draggable
              onDragStart={handleDragStart(index)}
              onDragOver={handleDragOver(index)}
              onDrop={handleDrop(index)}
              onDragEnd={handleDragEnd}
              onClick={() => onSelect(index)}
              className={`group w-full text-left rounded-lg border p-3 transition-all ${
                isSelected
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 ring-1 ring-blue-500'
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 bg-white dark:bg-gray-800'
              } ${isDragging ? 'opacity-40' : ''} ${isDragOver ? 'border-blue-400 border-dashed' : ''}`}
            >
              <div className="flex items-center gap-2">
                <svg
                  className="h-4 w-4 flex-shrink-0 text-gray-300 dark:text-gray-600 cursor-grab"
                  fill="none" viewBox="0 0 24 24" stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                </svg>
                <span className="flex-1 min-w-0 truncate text-sm font-medium text-gray-900 dark:text-white">
                  {tab.name || 'Sans nom'}
                </span>
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-1.5 pl-6">
                <span className={typeBadgeClass}>
                  {TAB_TYPES[tab.type]?.label || tab.type}
                </span>
                {tab.group && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300">
                    {tab.group}
                  </span>
                )}
                {tab.admin_only && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200">
                    Admin
                  </span>
                )}
                {!validation.valid && (
                  <span
                    title={describeConfigError(validation.error, tabs)}
                    className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300"
                  >
                    À corriger
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>

      <button
        type="button"
        onClick={onAdd}
        className="mt-3 w-full px-3 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center justify-center"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
        Ajouter un onglet
      </button>
    </div>
  );
};

export default TabList;
